/**
 * LaGift — quick add
 * ---------------------------------------------------------------------------
 * Adds a single-variant product to the cart from a product card, and then hands
 * off to whichever cart UI the store is configured for.
 *
 * The cart type is not assumed. Shopify offers three, and each has a different
 * contract in Dawn:
 *
 *   drawer        <cart-drawer> exists; render the cart-drawer and
 *                 cart-icon-bubble sections into it and it opens itself.
 *   notification  <cart-notification> exists; render the notification product,
 *                 its button, and cart-icon-bubble.
 *   page          No cart UI on the page at all; go to /cart.
 *
 * Asking for the right sections in the add request means the cart re-renders
 * from one round trip instead of a second fetch.
 *
 * We also publish Dawn's `cart-update` event, so anything else listening - the
 * cart page, a free-shipping bar - stays in step. `publish` and PUB_SUB_EVENTS
 * come from Dawn's pubsub.js and constants.js, which theme.liquid already loads.
 */
/**
 * Scoped so the file can run more than once.
 *
 * A `<script src>` executes once per tag, not once per URL. Several sections
 * include this, and a merchant can add the same section twice, so on a busy
 * page it runs repeatedly - and a top-level class or const throws
 * "Identifier ... has already been declared" on every run after the first.
 * Inside a function there is nothing shared to collide with.
 */
(() => {
  class LagiftQuickAdd extends HTMLElement {
    connectedCallback() {
      this.button = this.querySelector('[data-quick-add-button]');
      this.cartType = this.dataset.cartType || 'page';
      this.variantId = this.dataset.variantId;

      this.button?.addEventListener('click', (event) => {
        event.preventDefault();
        this.add();
      });
    }

    /** The cart element for this store's cart type, if the page has one. */
    get cartElement() {
      if (this.cartType === 'drawer') return document.querySelector('cart-drawer');
      if (this.cartType === 'notification') return document.querySelector('cart-notification');
      return null;
    }

    /**
     * Sections the cart UI needs back in order to re-render itself. These names
     * mirror each element's own getSectionsToRender(), so if Dawn's cart markup
     * changes we are asking for the same things it is.
     */
    get sections() {
      if (this.cartType === 'drawer') return ['cart-drawer', 'cart-icon-bubble'];
      if (this.cartType === 'notification') {
        return ['cart-notification-product', 'cart-notification-button', 'cart-icon-bubble'];
      }
      return [];
    }

    async add() {
      if (!this.variantId || this.classList.contains('is-loading')) return;

      const cart = this.cartElement;

      // No cart UI on this page: the honest outcome is the cart page itself,
      // rather than adding silently with nothing to show for it.
      if (!cart) {
        window.location = window.routes?.cart_url || '/cart';
        return;
      }

      this.setLoading(true);

      const body = {
        items: [{ id: Number(this.variantId), quantity: 1 }],
        sections: this.sections.join(','),
        sections_url: window.location.pathname,
      };

      try {
        const response = await fetch(`${window.routes?.cart_add_url || '/cart/add'}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' },
          body: JSON.stringify(body),
        });

        const result = await response.json();

        if (result.status) {
          // Shopify returns a status field only on failure.
          this.showError(result.description || result.message);
          return;
        }

        // /cart/add.js returns { items: [...] } for a multi-item add; the cart
        // elements expect the single line item's shape.
        const item = result.items ? result.items[0] : result;
        cart.renderContents({ ...item, sections: result.sections });

        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'lagift-quick-add', productVariantId: this.variantId });
        }
      } catch (error) {
        console.error('[lagift-quick-add]', error);
        this.showError();
      } finally {
        this.setLoading(false);
      }
    }

    setLoading(loading) {
      this.classList.toggle('is-loading', loading);
      if (this.button) this.button.disabled = loading;
    }

    showError(message) {
      // Deliberately not a silent failure: if the add did not work the customer
      // needs to know, and the product page can always complete the job.
      this.classList.add('has-error');
      if (message && this.button) this.button.setAttribute('title', message);
      setTimeout(() => this.classList.remove('has-error'), 2000);
    }
  }

  if (!customElements.get('lagift-quick-add')) {
    customElements.define('lagift-quick-add', LagiftQuickAdd);
  }
})();
